/**
 * editorEl 대상 DOM 조작 유틸(순수 함수) — 편집가능 토글 / 컬럼 정규화 / 표시용 URL 해석.
 * 모두 editorEl 을 인자로 받아 상태를 갖지 않는다(테스트·재사용 용이).
 */
import { renderBlock } from '../serialize/document.js';
import { DEFAULT_BLOCK_PROPS } from './blocks.js';

/** 읽기전용 토글 — contenteditable 을 일괄 on/off 하고 복원 마커를 남긴다. */
export function applyEditable(editorEl, on) {
  editorEl.classList.toggle('lumir-readonly', !on);
  if (!on) {
    editorEl.querySelectorAll('[contenteditable="true"]').forEach((el) => {
      el.setAttribute('contenteditable', 'false');
      el.setAttribute('data-was-editable', '1');
    });
  } else {
    editorEl.querySelectorAll('[data-was-editable="1"]').forEach((el) => {
      el.setAttribute('contenteditable', 'true');
      el.removeAttribute('data-was-editable');
    });
  }
}

/**
 * columnList 정규화 — 2개 미만은 펼쳐 제거, 2개 초과(3단+)는 2단으로 병합(2단까지만 지원).
 */
export function normalizeColumnsDom(editorEl) {
  editorEl.querySelectorAll('.block[data-type="columnList"]').forEach((cl) => {
    const content = cl.querySelector(':scope > .content');
    const cols = [...cl.querySelectorAll(':scope > .content > .lumir-column')];
    if (cols.length < 2) {
      const frag = document.createDocumentFragment();
      cols.forEach((col) => {
        [...col.children]
          .filter((b) => b.classList && b.classList.contains('block'))
          .forEach((b) => frag.appendChild(b));
      });
      if (!frag.childNodes.length)
        frag.appendChild(
          renderBlock({
            type: 'paragraph',
            props: DEFAULT_BLOCK_PROPS(),
            content: [],
            children: [],
          }),
        );
      cl.replaceWith(frag);
      return;
    }
    if (cols.length > 2 && content) {
      // 3단 이상: 3번째 이후 컬럼의 블록을 2번째 컬럼으로 이동 후, 초과 컬럼·여분 핸들 제거.
      const second = cols[1];
      for (let i = 2; i < cols.length; i++) {
        [...cols[i].children]
          .filter((b) => b.classList && b.classList.contains('block'))
          .forEach((b) => second.appendChild(b));
      }
      let node = second.nextSibling;
      while (node) {
        const next = node.nextSibling;
        content.removeChild(node);
        node = next;
      }
    }
  });
}

/**
 * A8: resolveFileUrl — 표시 전용 URL 해석(인증 토큰/사설 스킴).
 * __props.url(원본)은 그대로 두어 직렬화에 영향을 주지 않는다.
 */
export function applyResolveFileUrl(editorEl, resolveFileUrl) {
  if (typeof resolveFileUrl !== 'function') return;
  editorEl
    .querySelectorAll(
      '.block[data-type="image"],.block[data-type="video"],.block[data-type="audio"],.block[data-type="file"]',
    )
    .forEach((el) => {
      const props = el.__props || {};
      if (!props.url) return;
      const type = el.dataset.type;
      const setSrc = (resolved) => {
        if (resolved == null || resolved === props.url) return;
        if (type === 'image') {
          const img = el.querySelector('.lumir-image img');
          if (img) img.src = resolved;
          const nc = el.querySelector('.lumir-image-namecard');
          if (nc) nc.href = resolved;
        } else if (type === 'video') {
          const v = el.querySelector('video');
          if (v) v.src = resolved;
        } else if (type === 'audio') {
          const a = el.querySelector('audio');
          if (a) a.src = resolved;
        } else if (type === 'file') {
          const d = el.querySelector('.lf-download');
          if (d) d.href = resolved;
        }
      };
      try {
        Promise.resolve(resolveFileUrl(props.url, { type }))
          .then(setSrc)
          .catch(() => {});
      } catch (_) {}
    });
}
